import type { UserType, UserOrganisationType } from "@/api/query/useCurrentUserQuery";

/**
 * Safely retrieve the org-level role for the **current** organisation.
 *
 * Replaces the fragile `user.userOrganisation[0]?.role` pattern that
 * assumes the first element is always the active org.
 *
 * Resolution order:
 *  1. If `organisationId` is provided, find the matching entry.
 *  2. Otherwise fall back to the first entry (single-org scenario).
 *
 * Performance: O(n) linear scan on a typically 1–3 element array.
 */
export function getCurrentOrgRole(
  user: UserType | null | undefined,
  organisationId?: string,
): UserOrganisationType["role"] | undefined {
  if (!user?.userOrganisation?.length) return undefined;

  if (organisationId) {
    return user.userOrganisation.find(
      (uo) => uo.organisationId === organisationId,
    )?.role;
  }

  // Fallback: first org — safe when user belongs to exactly 1 org
  return user.userOrganisation[0]?.role;
}

/**
 * Retrieve the full UserOrganisation record for the current org.
 * Useful when callers need more than just the role (e.g. organisationId, jobTitle).
 */
export function getCurrentUserOrganisation(
  user: UserType | null | undefined,
  organisationId?: string,
): UserOrganisationType | undefined {
  if (!user?.userOrganisation?.length) return undefined;

  if (organisationId) {
    return user.userOrganisation.find(
      (uo) => uo.organisationId === organisationId,
    );
  }

  return user.userOrganisation[0];
}

/**
 * Check if the user holds any of the target roles across their organisations.
 * If organisationId is specified, checks that specific organisation;
 * otherwise, checks across all user organisations.
 */
export function userHasAnyOrgRole(
  user: UserType | null | undefined,
  targetRoles: Array<UserOrganisationType["role"]>,
  organisationId?: string,
): boolean {
  if (!user?.userOrganisation?.length) return false;
  if (organisationId) {
    const org = user.userOrganisation.find(
      (uo) => uo.organisationId === organisationId,
    );
    return Boolean(org && targetRoles.includes(org.role));
  }
  return user.userOrganisation.some((uo) => targetRoles.includes(uo.role));
}
